import React, { useCallback, useEffect, useRef, useState } from 'react';
import Masonry from "react-masonry-component";
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Select from "react-select";
import ReactTable from "react-table";
import i18next from "i18next";
import { Message } from '../Message';
import { PageTitle } from '../PageTitle';
import { PastChecklistsService, TagsService, TimeFormattingService } from '../../services';
import { AccessHelper, BaseTable } from '../../helpers';
import moment from 'moment-timezone';
import { DatePickerInput } from '../DatePickerInput';
import QueryString from "query-string";

const MASONRY_OPTIONS = {
  itemSelector: ".masonry-item",
  transitionDuration: 0,
  percentPosition: true,
  columnWidth: ".masonry-sizer",
  gutter: ".gutter-sizer",
  stagger: 30,
};

const NormalTime = ({ value, runtime, timezone }) => {
  if (value) {    
    const date = TimeFormattingService.renderDate(value, timezone);
    const time = TimeFormattingService.renderTime(value, timezone);
    if (runtime) {
      const runtimeInMinutes = runtime / 1000 / 60;
      const runtimeStr = runtimeInMinutes <= 240 ? `${(runtime / 1000 / 60).toFixed(0)} minutes` : '> 4 hours';
      return <span>{date}<br/>{time}<br/>{runtimeStr}</span>;
    } else {
      return <span>{date}<br/>{time}</span>;
    }
  } else {
    return <span/>;
  }
};

const InProgressChecklistsArea = ({ grouping, weekStartingAt, filterByTagId, onError, canViewChecklistDetail, onPastChecklistClicked }) => {
  const [tableData, setTableData] = useState({ data: [], pageSize: 5, pageCount: 0, loading: false, totalElements: 0 });
  const tableRef = useRef();

  const DEFAULT_SORTED = [{ id: "endTime", desc: true }];

  useEffect(() => {
      tableRef.current.fireFetchData();
  }, [weekStartingAt]);

  const fetchData = useCallback((inTableData) => {
    if (inTableData.loading) {
      return;
    }
    
    setTableData({ ...inTableData, loading: true });

    const options = BaseTable.buildURLOptions(inTableData, DEFAULT_SORTED);

    options.push({weekStartingAt: weekStartingAt});
    options.push({state: 1});
    if (filterByTagId != -1) {
      options.push({filterByTagId: filterByTagId});
    }

    PastChecklistsService.getLocationChecklistDashboardPastData(grouping.id, options)
      .then((response) => {
        setTableData({ 
          ...inTableData, 
          data: response.report.content, 
          pageCount: response.report.totalPages, 
          totalElements: response.report.totalElements, 
          loading: false 
        });
      }).catch(error => {
        console.error(error);
        
        setTableData({ ...inTableData, data: [], pageCount: 0, totalElements: 0, loading: false });

        onError(error.message);
      });
  }, [grouping, weekStartingAt, onError]);

  return (
    <div>
      <div className="d-region">
        <div className="l-region__heading">
          <h1>Incomplete Checklists</h1> 
        </div>
      </div>

      <div className="react-table-custom">
        <ReactTable
          pageSizeOptions={[5, 10, 25]}
          className="-striped -highlight"
          ref={tableRef}
          pageSize={tableData.pageSize}
          manual // Forces table not to paginate or sort automatically, so we can handle it server-side
          data={tableData.data}
          pages={tableData.pageCount} // Display the total number of pages
          loading={tableData.loading} // Display the loading overlay when we need it
          totalElements={tableData.totalElements}
          page={tableData.page}
          onFetchData={fetchData} // Request new data when things change
          showPaginationTop
          defaultSorted={DEFAULT_SORTED}
          columns={[
            {
              Header: "Checklist Name",
              Cell: ({original}) => { 
                return (
                          canViewChecklistDetail ? 
                            <div className="pull-left">
                              <button className='btn btn-sm btn-link' style={{padding:0}} onClick={() => onPastChecklistClicked(original.pastChecklistId)}>{original.checklistName}</button> (V{original.checklistVersion})
                            </div> : 
                            <span>{original.checklistName} (V{original.checklistVersion})</span>
                )
              },
              sortable: false,
              minResizeWidth: 50,
              width: 400
            },
            {
              Header: "Tags",
              accessor: "tagNames",
              Cell: ({value}) => <span>{value.join(', ')}</span>,
              sortable: false,
              minResizeWidth: 50,
              width: 170
            },
            {
              Header: "Tasks Completed",
              Cell: ({original}) => <span>{(100*(original.completedTasksCount/original.totalTasksCount)).toFixed(0)}% - {original.completedTasksCount} of {original.totalTasksCount}</span>,
              sortable: false,
              minResizeWidth: 50,
              width: 170
            },
            {
              Header: "Started",
              accessor: "startTime",
              Cell: ({original}) => <NormalTime value={original.startTime} runtime={new Date() - original.startTime} timezone={grouping.timezone} />,
              sortable: true,
              minResizeWidth: 50,
              width: 150
            },
            {
              Header: "Corrective Actions",
              Cell: ({original}) => original.requiredCorrectiveActionsCount == 0 ? <span>None</span> : <span>{original.completedCorrectiveActionsCount} of {original.requiredCorrectiveActionsCount} complete</span>,
              sortable: false,
              minResizeWidth: 50
            },
            {
              Header: "Completed By",
              accessor: "completedBy",
              sortable: false,
              minResizeWidth: 50
            }
          ]}
        />
      </div>
    </div>
  );
}

const CompletedChecklistsArea = ({ grouping, weekStartingAt, filterByTagId, onError, canViewChecklistDetail, onPastChecklistClicked }) => {
  const [tableData, setTableData] = useState({ data: [], pageSize: 5, pageCount: 0, loading: false, totalElements: 0 });
  const tableRef = useRef();

  const DEFAULT_SORTED = [{ id: "endTime", desc: true }];

  useEffect(() => {
    tableRef.current.fireFetchData();
  }, [weekStartingAt]);

  const fetchData = useCallback((inTableData) => {
    if (inTableData.loading) {
      return;
    }

    setTableData({ ...inTableData, loading: true });

    const options = BaseTable.buildURLOptions(inTableData, DEFAULT_SORTED);

    options.push({weekStartingAt: weekStartingAt});
    options.push({state: 2});
    if (filterByTagId != -1) {
      options.push({filterByTagId: filterByTagId});
    }

    PastChecklistsService.getLocationChecklistDashboardPastData(grouping.id, options)
      .then((response) => {
        setTableData({ 
          ...inTableData, 
          data: response.report.content, 
          pageCount: response.report.totalPages,
          totalElements: response.report.totalElements, 
          loading: false 
        });
      }).catch(error => {
        console.error(error);
        
        setTableData({ ...inTableData, data: [], pageCount: 0, totalElements: 0, loading: false });
        
        onError(error.message);
      });
  }, [grouping, weekStartingAt, onError]);

  return (
    <div>
      <div className="d-region">
        <div className="l-region__heading">
          <h1>Completed Checklists</h1> 
        </div>
      </div>

      <div className="react-table-custom">
        <ReactTable
          pageSizeOptions={[5, 10, 25]}
          className="-striped -highlight"
          ref={tableRef}
          pageSize={tableData.pageSize}
          manual // Forces table not to paginate or sort automatically, so we can handle it server-side
          data={tableData.data}
          pages={tableData.pageCount} // Display the total number of pages
          loading={tableData.loading} // Display the loading overlay when we need it
          totalElements={tableData.totalElements}
          page={tableData.page}
          onFetchData={fetchData} // Request new data when things change
          showPaginationTop
          defaultSorted={DEFAULT_SORTED}
          columns={[
            {
              Header: "Checklist Name",
              Cell: ({original}) => { 
                return (
                          canViewChecklistDetail ? 
                            <div className="pull-left">
                              <button className='btn btn-sm btn-link'  style={{padding:0}} onClick={() => onPastChecklistClicked(original.pastChecklistId)}>{original.checklistName}</button> (V{original.checklistVersion})
                            </div> :
                            <span>{original.checklistName} (V{original.checklistVersion})</span>
                )
              },
              sortable: false,
              minResizeWidth: 50,
              width: 290         
            },
            {
              Header: "Tags",
              accessor: "tagNames",
              Cell: ({value}) => <span>{value.join(', ')}</span>,
              sortable: false,
              minResizeWidth: 50,
              width: 160
            },
            {
              Header: "Tasks Completed",
              Cell: ({original}) => <span>{(100*(original.completedTasksCount/original.totalTasksCount)).toFixed(0)}% - {original.completedTasksCount} of {original.totalTasksCount}</span>,
              sortable: false,
              minResizeWidth: 50,
              width: 160
            },
            {
              Header: "Started",
              accessor: "startTime",
              Cell: ({original}) => <NormalTime value={original.startTime} timezone={grouping.timezone} />,
              sortable: true,
              minResizeWidth: 50,
              width: 140
            },
            {
              Header: "Ended",
              accessor: "endTime",
              Cell: ({original}) => <NormalTime value={original.endTime} runtime={original.endTime - original.startTime} timezone={grouping.timezone} />,
              sortable: true,
              minResizeWidth: 50,
              width: 140
            },
            {
              Header: "Corrective Actions",
              Cell: ({original}) => original.requiredCorrectiveActionsCount == 0 ? <span>None</span> : <span>{original.completedCorrectiveActionsCount} of {original.requiredCorrectiveActionsCount} complete</span>,
              sortable: false,
              minResizeWidth: 50
            },
            {
              Header: "Completed By",
              accessor: "completedBy",
              sortable: false,
              minResizeWidth: 50
            }
          ]}
        />
      </div>
    </div>
  );
}

const AbandonedChecklistsArea = ({ grouping, weekStartingAt, filterByTagId, onError, canViewChecklistDetail, onPastChecklistClicked }) => {
  const [tableData, setTableData] = useState({ data: [], pageSize: 5, pageCount: 0, loading: false, totalElements: 0 });
  const tableRef = useRef();

  const DEFAULT_SORTED = [{ id: "endTime", desc: true }];

  useEffect(() => {
      if (tableRef?.current) {
        tableRef.current.fireFetchData();
      } else {
        fetchData(tableData);
      }
  }, [weekStartingAt]);

  const fetchData = useCallback((inTableData) => {
    if (inTableData.loading) {
      return;
    }

    setTableData({ ...inTableData, loading: true });

    const options = BaseTable.buildURLOptions(inTableData, DEFAULT_SORTED);

    options.push({weekStartingAt: weekStartingAt});
    options.push({state: 3});
    if (filterByTagId != -1) {
      options.push({filterByTagId: filterByTagId});
    }

    PastChecklistsService.getLocationChecklistDashboardPastData(grouping.id, options)
      .then((response) => {
        setTableData({ 
          ...inTableData, 
          data: response.report.content, 
          pageCount: response.report.totalPages, 
          totalElements: response.report.totalElements, 
          loading: false 
        });
      }).catch(error => {
        console.error(error);
        
        setTableData({ ...inTableData, data: [], pageCount: 0, totalElements: 0, loading: false });

        onError(error.message);
      });
  }, [grouping, weekStartingAt, onError]);

  return tableData.data.length > 0 && (
    <div>
      <div className="d-region">
        <div className="l-region__heading">
          <h1>Abandoned Checklists</h1> 
        </div>
      </div>

      <div className="react-table-custom">
        <ReactTable
          pageSizeOptions={[5, 10, 25]}
          className="-striped -highlight"
          ref={tableRef}
          pageSize={tableData.pageSize}
          manual // Forces table not to paginate or sort automatically, so we can handle it server-side
          data={tableData.data}
          pages={tableData.pageCount} // Display the total number of pages
          loading={tableData.loading} // Display the loading overlay when we need it
          totalElements={tableData.totalElements}
          page={tableData.page}
          onFetchData={fetchData} // Request new data when things change
          showPaginationTop
          defaultSorted={DEFAULT_SORTED}
          columns={[
            {
              Header: "Checklist Name",
              Cell: ({original}) => { 
                return (
                          canViewChecklistDetail ? 
                            <div className="pull-left">
                              <button className='btn btn-sm btn-link'  style={{padding:0}} onClick={() => onPastChecklistClicked(original.pastChecklistId)}>{original.checklistName}</button> (V{original.checklistVersion})
                            </div> : 
                            <span>{original.checklistName} (V{original.checklistVersion})</span>
                )
              },
              sortable: false,
              minResizeWidth: 50,
              width: 400
            },
            {
              Header: "Tags",
              accessor: "tagNames",
              Cell: ({value}) => <span>{value.join(', ')}</span>,
              sortable: false,
              minResizeWidth: 50,
              width: 170
            },
            {
              Header: "Tasks Completed",
              Cell: ({original}) => <span>{(100*(original.completedTasksCount/original.totalTasksCount)).toFixed(0)}% - {original.completedTasksCount} of {original.totalTasksCount}</span>,
              sortable: false,
              minResizeWidth: 50,
              width: 170
            },
            {
              Header: "Started",
              accessor: "startTime",
              Cell: ({original}) => <NormalTime value={original.startTime} runtime={new Date() - original.startTime} timezone={grouping.timezone} />,
              sortable: true,
              minResizeWidth: 50,
              width: 150
            },
            {
              Header: "Corrective Actions",
              Cell: ({original}) => original.requiredCorrectiveActionsCount == 0 ? <span>None</span> : <span>{original.completedCorrectiveActionsCount} of {original.requiredCorrectiveActionsCount} complete</span>,
              sortable: false,
              minResizeWidth: 50
            },
            {
              Header: "Completed By",
              accessor: "completedBy",
              sortable: false,
              minResizeWidth: 50
            }
          ]}
        />
      </div>
    </div>
  );
}

const AvailableChecklistsArea = ({ grouping, weekStartingAt, filterByTag, onError, canViewChecklistConfig, onChecklistConfigClicked }) => {
  const [availableChecklists, setAvailableChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    setLoading(true);

    PastChecklistsService.getLocationChecklistDashboardAvailableData(grouping.id, [{ requestName: "available" }, { weekStartingAt: weekStartingAt }])
      .then((response) => {
        if (filterByTag.value != -1) {
          setAvailableChecklists(response.availableChecklists.filter((checklist) => checklist.tagNames.includes(filterByTag?.label)));
        } else {
          setAvailableChecklists(response.availableChecklists);
        }
        setLoading(false);
      })
      .catch((error) =>{
        console.error(error);
        setLoading(false);
        onError(error.message);
      });
  }, [grouping, onError, weekStartingAt, filterByTag]);

  return (
    <div>
      <div className="d-region">
        <div className="l-region__heading">
          <h1>Available Checklists</h1> 
        </div>
      </div>

      <div className="d-region">
        <Masonry options={MASONRY_OPTIONS}>
          <div className="masonry-sizer" />
          <div className="gutter-sizer" />

          { loading && <div className="loader">{ i18next.t('loading') }</div>}
          { !loading && availableChecklists.length === 0 && <div>No available checklists</div> }
          { 
            !loading && availableChecklists.length > 0 && availableChecklists.map((checklist) => {
              return (
                <div className="masonry-item" key={checklist.checklistId}>
                  <div className="dash-card-region">
                    <div className="card-region__header locations__header home-dash">
                      <div className="location-name" style={{ cursor: canViewChecklistConfig ? "pointer" : "", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }} onClick={() => { onChecklistConfigClicked(checklist.checklistId); }}>
                        {checklist.checklistName}
                      </div>
                    </div>
                    <div className="card-region__cards">
                      <div className="other-info">Tasks: {checklist.taskCount}</div>
                      <div className="other-info">Corrective Actions For Week: {checklist.weeklyCorrectiveActionsCount}</div>
                      <div className="other-info">Started/Completed For Week: {checklist.weeklyStartedCount}/{checklist.weeklyCompletedCount} {checklist.weeklyStartedCount == 0 ? '(0%)' : `(${Math.round((checklist.weeklyCompletedCount/checklist.weeklyStartedCount) * 100.0)}%)`}</div>
                      <div className="other-info">Last Performed: { checklist.lastPerformedAt ? TimeFormattingService.renderFancyDateTime(checklist.lastPerformedAt, grouping.timezone) : 'Never'}</div>
                      <div className="other-info">Version: {checklist.checklistVersion}</div>
                      <div className="other-info">Tags: {checklist.tagNames.length > 0 ? checklist.tagNames.join(", ") : "None"}</div>
                    </div>
                  </div>
                </div>
              );
            }) 
          }
        </Masonry>
      </div>
    </div>
  );
}

const MonitorChecklistsList = ({ me, grouping, history }) => {
  const query = QueryString.parse(history?.location?.search);
  const weekStartingAt = query.week ? new Date(moment(parseInt(query.week) * 1000).startOf("week").valueOf()) : new Date(moment().startOf("week").valueOf());

  const canViewChecklistDetail = AccessHelper.userHasAccess(me.availableRoutes, "/monitoring/locations/:deviceGroupingId/checklists/:checklistId");
  const canViewChecklistConfig = AccessHelper.userHasAccess(me.availableRoutes, "/config/checklists");

  const [localErrorMessage, setLocalErrorMessage] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [availableTags, setAvailableTags] = useState(null);

  const updateWeekStartingAt = (dayValue) => {
    const updatedWeekStartingAt = moment(dayValue).startOf("week");
    const selectedWeekTS = updatedWeekStartingAt.valueOf() / 1000;
    if (!query.week || parseInt(query.week) != selectedWeekTS) {
      history.push({ search: `week=${selectedWeekTS}&selectedTagId=${selectedTag?.value ?? -1}` });
    }
  };

  useEffect(() => {
    if (selectedTag) {
      if (!query.selectedTagId || parseInt(query.selectedTagId) != (selectedTag?.value ?? -1)) {
        history.push({ search: `${query.week ? `week=${query.week}&` : ''}selectedTagId=${selectedTag?.value ?? -1}` });
      }
    }
  }, [selectedTag]);

  useEffect(() => {
    TagsService.listAll().then((response) => {
      const tags = [
        { value: -1, label: 'Any', tag: {id: -1} }
      ].concat(response.tags.filter((tag) => tag.tagType.token == "checklist-type").map((tag) => ({ value: tag.id, label: tag.name, tag: tag })));
      
      if (query.selectedTagId) {
        setSelectedTag(tags.find((tag) => tag.value == query.selectedTagId));
      } else {
        setSelectedTag(tags[0]);
      }
      
      setAvailableTags(tags);
    }).catch((error) => {
      console.error(error);
      setLocalErrorMessage(error);
    });
  }, []);

  if (redirectTo) {
    return redirectTo.pastChecklistId ? 
              <Redirect push to={{ pathname: `/monitoring/locations/${grouping.id}/checklists/${redirectTo.pastChecklistId}`, state: query.week ? { backLinkSearch: `?week=${query.week}` } : {} }}/> : 
              <Redirect push to={{ pathname: `/config/checklists/view/${redirectTo.checklistConfigId}`, state: query.week ? { backLinkSearch: `?week=${query.week}` } : {} }}/>
  } else {
    return availableTags && selectedTag ? (
      <div className="animated fadeIn needs-attention-dash">
        <div className="row">
          <PageTitle title={ i18next.t('pages.checklists.header') }/>
        </div>
        {
          localErrorMessage && <Message errorMessage={localErrorMessage} messageDismissCallback={() => setLocalErrorMessage(null)} />
        }

        <div className="op-content-box">
          <div className='row'>
            <div className="col-sm-3">
              <label>Week Starting Date</label>
              <DatePickerInput 
                input={{ value: weekStartingAt }} 
                meta={{}} 
                onDayChange={updateWeekStartingAt} 
                dayPickerProps={{
                    disabledDays: {daysOfWeek: [1,2,3,4,5,6]}
                }}
              />
            </div>
            <div className="col-sm-3">
              <label>Restrict Checklist by Tag</label>
              <Select
                options={availableTags}
                value={selectedTag}
                onChange={setSelectedTag}
                searchable={true}
                classNamePrefix="react-select"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">

                <div className="card-block">

                  <InProgressChecklistsArea
                    grouping={grouping}
                    weekStartingAt={weekStartingAt.getTime()}
                    filterByTagId={query.selectedTagId ?? -1}
                    onError={setLocalErrorMessage}
                    canViewChecklistDetail={canViewChecklistDetail}
                    onPastChecklistClicked={(pastChecklistId) => setRedirectTo({pastChecklistId: pastChecklistId})}
                  />

                  <CompletedChecklistsArea
                    grouping={grouping}
                    weekStartingAt={weekStartingAt.getTime()}
                    filterByTagId={query.selectedTagId ?? -1}
                    onError={setLocalErrorMessage}
                    canViewChecklistDetail={canViewChecklistDetail}
                    onPastChecklistClicked={(pastChecklistId) => setRedirectTo({pastChecklistId: pastChecklistId})}
                  />

                  <AbandonedChecklistsArea
                    grouping={grouping}
                    weekStartingAt={weekStartingAt.getTime()}
                    filterByTagId={query.selectedTagId ?? -1}
                    onError={setLocalErrorMessage}
                    canViewChecklistDetail={canViewChecklistDetail}
                    onPastChecklistClicked={(pastChecklistId) => setRedirectTo({pastChecklistId: pastChecklistId})}
                  />

                  <AvailableChecklistsArea 
                    grouping={grouping}
                    weekStartingAt={weekStartingAt.getTime()}
                    onError={setLocalErrorMessage}
                    filterByTag={selectedTag}
                    canViewChecklistConfig={canViewChecklistConfig}
                    onChecklistConfigClicked={(checklistConfigId) => setRedirectTo({checklistConfigId: checklistConfigId})}
                  />

                </div>

            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="loader">{i18next.t("loading")}</div>
    );
  }
}

export default connect(({ user }) => ({ me: user.me }))(MonitorChecklistsList);
